import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Repeat,
  Loader2,
  Play,
  Square,
  RefreshCw,
  Clock,
  AlertCircle,
  CheckCircle,
  MessageSquare,
  Send,
  Menu,
} from 'lucide-react';
import Badge from '../../components/ui/Badge';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useChatLayout } from '../../components/layout/ChatLayout';
import { RoutineForm } from '../../components/routines';
import type { RoutineFormSubmit } from '../../components/routines';
import { routinesApi } from '../../services/routines-api';
import { handleApiError } from '../../services/api';
import { humanReadableCron } from '../../utils/cron';
import {
  outputChannelsApi,
  type OutputChannelOption,
} from '../../services/output-channels-api';
import { useTimezone } from '../../contexts/TimezoneContext';
import toast from 'react-hot-toast';

const RoutineDetailPage: React.FC = () => {
  const { routineId } = useParams<{ routineId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatRelativeTime, timezone: orgTimezone } = useTimezone();
  const { isMobile, toggleSidebar } = useChatLayout();

  const [isEditing, setIsEditing] = useState(false);
  const isEditRoute = window.location.pathname.endsWith('/edit');

  const { data: routineData, isLoading: loadingRoutine, error: routineError } = useQuery({
    queryKey: ['routine', routineId],
    queryFn: () => routinesApi.getRoutine(routineId!),
    enabled: !!routineId,
  });

  const { data: outputChannelsData } = useQuery({
    queryKey: ['output-channels'],
    queryFn: () => outputChannelsApi.getOutputChannels(),
  });

  const routine = routineData?.routine;

  useEffect(() => {
    setIsEditing(isEditRoute);
  }, [isEditRoute]);

  const updateMutation = useMutation({
    mutationFn: (data: RoutineFormSubmit) =>
      routinesApi.updateRoutine(routineId!, {
        name: data.name,
        schedule: data.schedule,
        timezone: data.timezone,
        message: data.message,
        outputChannels: data.outputChannels,
        enabled: data.enabled,
      }),
    onSuccess: () => {
      toast.success('Routine updated');
      queryClient.invalidateQueries({ queryKey: ['routine', routineId] });
      queryClient.invalidateQueries({ queryKey: ['routines'] });
      setIsEditing(false);
      if (isEditRoute) {
        navigate(`/routines/${routineId}`);
      }
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to update routine');
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async (enable: boolean) => {
      return enable
        ? routinesApi.enableRoutine(routineId!)
        : routinesApi.disableRoutine(routineId!);
    },
    onSuccess: (_, enable) => {
      toast.success(`Routine ${enable ? 'enabled' : 'disabled'}`);
      queryClient.invalidateQueries({ queryKey: ['routine', routineId] });
      queryClient.invalidateQueries({ queryKey: ['routines'] });
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to update status');
    },
  });

  const runRoutineMutation = useMutation({
    mutationFn: () => routinesApi.executeRoutine(routineId!),
    onSuccess: (result) => {
      const threadId = result.data?.thread_id;
      if (threadId) {
        toast.success('Routine executed — opening chat thread...');
        navigate(`/chat/${threadId}`);
      } else {
        toast.success(result.message || 'Routine execution started');
      }
      queryClient.invalidateQueries({ queryKey: ['routine', routineId] });
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to run routine');
    },
  });

  if (loadingRoutine) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (routineError || !routine) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/routines')}
          className="flex items-center gap-2 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Routines
        </button>
        <div className="bg-destructive/15 border border-destructive/20 rounded-lg p-6">
          <h3 className="text-lg font-medium text-destructive mb-2">
            Routine Not Found
          </h3>
          <p className="text-destructive">
            The requested routine could not be found.
          </p>
        </div>
      </div>
    );
  }

  const isActive = routine.status === 'active';
  const renderChannelLabel = (cfg: { source: string; id: string }): string => {
    const opt: OutputChannelOption | undefined = outputChannelsData?.channels.find(
      (c) => c.source === cfg.source && c.id === cfg.id,
    );
    if (!opt) return `${cfg.source}:${cfg.id}`;
    return `${opt.label}${opt.detail ? ` — ${opt.detail}` : ''} (${opt.type.toUpperCase()})`;
  };

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          aria-label="Open sidebar"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}
      <div className="w-full max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/routines')}
              className="p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <Repeat className="w-8 h-8 text-primary" />
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-foreground">
                    {isEditing ? 'Edit Routine' : routine.name}
                  </h1>
                  <Badge variant={isActive ? 'success' : 'neutral'}>
                    {isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {!isEditing && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => runRoutineMutation.mutate()}
                disabled={runRoutineMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-input dark:border-white/10 text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
              >
                {runRoutineMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Run Now
              </button>
              <button
                onClick={() => toggleStatusMutation.mutate(!isActive)}
                disabled={toggleStatusMutation.isPending}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-input dark:border-white/10 hover:bg-muted disabled:opacity-50 transition-colors ${
                  isActive
                    ? 'text-warn hover:text-warn/80'
                    : 'text-success hover:text-success/80'
                }`}
              >
                {toggleStatusMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isActive ? (
                  <Square className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                {isActive ? 'Disable' : 'Enable'}
              </button>
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary transition-colors"
              >
                Edit
              </button>
            </div>
          )}
        </div>

        {!isEditing && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-xl bg-muted border border-border p-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Schedule</p>
                    <p className="font-medium text-foreground">
                      {humanReadableCron(routine.schedule)}
                    </p>
                    <p className="text-xs text-muted-foreground">{routine.timezone}</p>
                  </div>
                </div>
              </div>
              <div className="rounded-xl bg-muted border border-border p-4">
                <div className="flex items-center gap-3">
                  {routine.last_run ? (
                    <CheckCircle className="w-5 h-5 text-success" />
                  ) : (
                    <Clock className="w-5 h-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Last Run</p>
                    <p className="font-medium text-foreground">
                      {routine.last_run ? formatRelativeTime(routine.last_run) : 'Never'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="rounded-xl bg-muted border border-border p-4">
                <div className="flex items-center gap-3">
                  {routine.error_count > 0 ? (
                    <AlertCircle className="w-5 h-5 text-destructive" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-success" />
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Errors</p>
                    <p className="font-medium text-foreground">
                      {routine.error_count}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {routine.error_count > 0 && routine.last_error && (
              <div className="bg-destructive/15 border border-destructive/20 rounded-lg p-4">
                <div className="flex items-center gap-2 text-destructive mb-2">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">Last Error</span>
                </div>
                <p className="text-destructive">{routine.last_error}</p>
              </div>
            )}

            <div className="rounded-xl bg-muted border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Prompt
              </h2>
              <div className="bg-muted rounded-lg p-4">
                <p className="text-foreground whitespace-pre-wrap">
                  {routine.message}
                </p>
              </div>
            </div>

            <div className="rounded-xl bg-muted border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                <Send className="w-5 h-5" />
                Output Channels
              </h2>
              <div className="bg-muted rounded-lg p-4 space-y-2">
                <div className="text-sm text-primary">
                  Chat thread <span className="text-xs text-muted-foreground">(always)</span>
                </div>
                {routine.outputChannels.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No additional channels — chat-only.
                  </p>
                ) : (
                  routine.outputChannels.map((cfg) => (
                    <div key={`${cfg.source}:${cfg.id}`} className="text-sm text-foreground">
                      {renderChannelLabel(cfg)}
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {isEditing && (
          <RoutineForm
            initial={{
              name: routine.name,
              schedule: routine.schedule,
              timezone: routine.timezone,
              message: routine.message,
              outputChannels: routine.outputChannels,
              enabled: routine.status === 'active',
            }}
            organizationTimezone={orgTimezone}
            submitLabel="Save changes"
            showEnabledToggle
            busy={updateMutation.isPending}
            onSubmit={async (state) => {
              await updateMutation.mutateAsync(state);
            }}
            onCancel={() => {
              setIsEditing(false);
              if (isEditRoute) {
                navigate(`/routines/${routineId}`);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default RoutineDetailPage;
